/**
 * CampingSystem — Manages camping, resting, rations, foraging, and night encounters.
 *
 * Roles reduce night encounter chance and provide camp bonuses.
 * Rations are consumed each rest; shortfall causes HP damage.
 * NPC wages are paid each rest cycle.
 * Foraging can supplement rations using Nature/Survival skill checks.
 *
 * @module CampingSystem
 */

import DebugLog from '../debug/DebugLog.js';
import WorldState from '../world/WorldState.js';
import TimeSystem from './TimeSystem.js';

// ─── Biome forage DCs ─────────────────────────────────────────────────────────

const FORAGE_DC = {
  forest:    8,
  plains:    10,
  grass:     10,
  hills:     12,
  mountains: 14,
  mountain:  14,
  desert:    16,
  snow:      14,
  swamp:     12,
  coast:     10,
  sand:      16,
};

// ─── CampingSystem ────────────────────────────────────────────────────────────

const CampingSystem = {
  /**
   * Camp roles available.
   * Each role reduces encounter chance or provides a camp bonus.
   */
  ROLES: {
    scout: {
      label: 'Scout',
      description: 'Reduces encounter chance by 30%',
      skillBonus: 'perception',
    },
    cook: {
      label: 'Cook',
      description: 'Rations feed +1 extra person',
      skillBonus: 'survival',
    },
    guard: {
      label: 'Guard',
      description: 'Further reduces encounter chance; can intercept',
      skillBonus: 'athletics',
    },
    medic: {
      label: 'Medic',
      description: 'Heals party 25% max HP overnight',
      skillBonus: 'medicine',
    },
    engineer: {
      label: 'Engineer',
      description: 'Repairs vehicles; maintains camp equipment',
      skillBonus: 'crafting',
    },
  },

  /**
   * Current camp state (null when not camping).
   * @type {object|null}
   */
  _campState: null,

  // ─── Setup ────────────────────────────────────────────────────────────────

  /**
   * Set up camp. Assigns roles based on provided assignments.
   * @param {Array<{charId: string, role: string}>} roleAssignments
   * @param {object} [options] — { hasTent, hasAlarmWire, hasPalisade }
   * @returns {{ success: boolean, message: string, campState: object }}
   */
  setupCamp(roleAssignments = [], options = {}) {
    const assignments = [];
    const warnings = [];

    for (const { charId, role } of roleAssignments) {
      if (!charId || !role) {
        warnings.push(`Skipping invalid assignment: charId=${charId} role=${role}`);
        continue;
      }
      if (!this.ROLES[role]) {
        warnings.push(`Unknown role "${role}" for char ${charId} — skipped`);
        continue;
      }
      assignments.push({ charId, role });
    }

    // Resolve camp equipment — merge with WorldState.campEquipment as defaults
    const equipment = WorldState.campEquipment ?? {};
    const campOptions = {
      hasTent:       options.hasTent       ?? equipment.hasTent       ?? false,
      hasAlarmWire:  options.hasAlarmWire  ?? equipment.hasAlarmWire  ?? false,
      hasPalisade:   options.hasPalisade   ?? equipment.hasPalisade   ?? false,
    };

    this._campState = {
      active: true,
      assignments,
      options: campOptions,
      biome:       WorldState.currentBiome       ?? 'forest',
      regionLevel: WorldState.currentRegionLevel ?? 1,
      setupAt:     { ...TimeSystem.getTime() },
    };

    const encounterChance = this.getNightEncounterChance();
    const roleLabels = assignments.map(a => `${a.charId}:${a.role}`).join(', ');

    DebugLog.info(
      `[camp] Camp set up; roles=[${roleLabels}] ` +
      `hasTent=${campOptions.hasTent} hasAlarmWire=${campOptions.hasAlarmWire} ` +
      `hasPalisade=${campOptions.hasPalisade} encounterChance=${(encounterChance * 100).toFixed(1)}%`
    );

    const message = warnings.length
      ? `Camp set up with warnings: ${warnings.join('; ')}`
      : `Camp set up. Night encounter chance: ${(encounterChance * 100).toFixed(1)}%`;

    return { success: true, message, campState: { ...this._campState } };
  },

  // ─── Resting ──────────────────────────────────────────────────────────────

  /**
   * Rest until morning. Consumes rations, applies medic healing, rolls night
   * encounters, and advances time to 06:00 the next day.
   * @param {object} rng — RNG instance from createRNG()
   * @returns {{
   *   rationCost: number,
   *   healed: boolean,
   *   encounter: object|null,
   *   message: string,
   *   warnings: string[]
   * }}
   */
  restUntilMorning(rng) {
    const warnings = [];

    // Gather party characters; safe to call even if CharacterSystem not fully loaded
    /** @type {Array} */
    let partyChars = [];
    try {
      const CS = window.__characterSystem;
      partyChars = CS ? CS.getAll() : [];
    } catch {
      // CharacterSystem not available; proceed with empty party
    }

    // ── Consume rations ──────────────────────────────────────────────────────
    const rationResult = this.consumeRations(partyChars);
    warnings.push(...rationResult.warnings);

    // ── Pay NPC wages ────────────────────────────────────────────────────────
    const wageResult = this.payWages(partyChars);
    if (wageResult.shortfall > 0) {
      warnings.push(
        `Cannot pay wages — shortfall: ${wageResult.shortfall} gold. Morale may drop.`
      );
    }

    // ── Medic healing ────────────────────────────────────────────────────────
    let healed = false;
    const hasMedic = this._campState?.assignments?.some(a => a.role === 'medic') ?? false;

    if (hasMedic && partyChars.length > 0) {
      for (const char of partyChars) {
        if (char.maxHP && char.hp !== undefined) {
          const healAmt = Math.floor(char.maxHP * 0.25);
          char.hp = Math.min(char.maxHP, (char.hp ?? 0) + healAmt);
        }
      }
      healed = true;
      DebugLog.info('[camp] Medic role: healed party 25% max HP overnight');
    }

    // ── Night encounter roll ──────────────────────────────────────────────────
    let encounter = null;
    const encounterChance = this.getNightEncounterChance();
    const roll = rng?.nextFloat?.() ?? Math.random();

    if (roll < encounterChance) {
      // Delegate to EncounterSystem via window optional chaining to avoid circular deps
      const ES = window.__encounterSystem;
      if (ES?.checkMovementEncounter) {
        try {
          encounter = ES.checkMovementEncounter(
            WorldState.currentBiome ?? 'forest',
            WorldState.currentRegionLevel ?? 1,
            rng
          );
        } catch (err) {
          DebugLog.warn(`[camp] Night encounter roll failed: ${err.message}`);
        }
      } else {
        // Return a minimal placeholder encounter so callers can handle the fact
        // that an encounter occurred even without the full EncounterSystem loaded.
        encounter = {
          type: 'combat',
          source: 'night_ambush',
          message: 'You are ambushed in the night!',
        };
      }

      DebugLog.event(
        `[camp] Night encounter triggered; roll=${roll.toFixed(3)} chance=${encounterChance.toFixed(3)} ` +
        `encounter=${JSON.stringify(encounter)}`
      );
    }

    // ── Advance time to 06:00 next day ───────────────────────────────────────
    const time = TimeSystem.getTime();
    const currentHour   = time.hour;
    const currentMinute = time.minute;

    // Hours until 06:00 next morning
    let hoursUntilDawn;
    if (currentHour < 6) {
      // Already past midnight — advance to 06:00 today
      hoursUntilDawn = 6 - currentHour - currentMinute / 60;
    } else {
      // Evening or later — advance past midnight to 06:00 next day
      hoursUntilDawn = (24 - currentHour) + 6 - currentMinute / 60;
    }

    TimeSystem.advanceTime(Math.max(0.5, hoursUntilDawn));

    DebugLog.info(
      `[camp] Rested until morning; rationCost=${rationResult.consumed} ` +
      `shortfall=${rationResult.shortfall} healed=${healed} encounter=${encounter !== null} ` +
      `wagesPaid=${wageResult.paid}`
    );

    const messageParts = [`Rested until morning. Rations consumed: ${rationResult.consumed}.`];
    if (healed) messageParts.push('Medic healed the party.');
    if (encounter) messageParts.push('A night encounter occurred!');
    if (warnings.length) messageParts.push(`Warnings: ${warnings.join('; ')}`);

    return {
      rationCost: rationResult.consumed,
      healed,
      encounter,
      message: messageParts.join(' '),
      warnings,
    };
  },

  // ─── Rations ──────────────────────────────────────────────────────────────

  /**
   * Consume daily rations for party.
   * 1 ration point per character per day.
   * If a cook is assigned, cost is reduced to 0.75 per character (rounded up).
   * If rations run out, party loses 10% max HP.
   * @param {Array} partyChars
   * @returns {{ consumed: number, shortfall: number, warnings: string[] }}
   */
  consumeRations(partyChars) {
    const warnings = [];
    const hasCook = this._campState?.assignments?.some(a => a.role === 'cook') ?? false;
    const costPerChar = hasCook ? 0.75 : 1.0;
    const rawCost = (partyChars.length || 1) * costPerChar;
    // Round to nearest integer (ceil) so fractional rations are always whole numbers
    const consumed = Math.ceil(rawCost);
    const available = WorldState.rationPoints ?? 0;

    if (consumed <= available) {
      WorldState.rationPoints = available - consumed;
      return { consumed, shortfall: 0, warnings };
    }

    // Partial or zero rations
    const shortfall = consumed - available;
    WorldState.rationPoints = 0;

    warnings.push(
      `Ration shortfall: ${shortfall} point(s). Party is hungry — each member loses 10% max HP.`
    );

    // Hungry penalty: 10% max HP per character
    for (const char of partyChars) {
      if (char.maxHP && char.hp !== undefined) {
        const dmg = Math.max(1, Math.floor(char.maxHP * 0.10));
        char.hp = Math.max(0, char.hp - dmg);
        DebugLog.warn(
          `[camp] ${char.name} is hungry — took ${dmg} HP damage (hp=${char.hp}/${char.maxHP})`
        );
      }
    }

    DebugLog.warn(
      `[camp] Ration shortfall; consumed=${consumed} available=${available} shortfall=${shortfall}`
    );

    return { consumed: available, shortfall, warnings };
  },

  // ─── Wages ────────────────────────────────────────────────────────────────

  /**
   * Pay NPC wages (gold per hired NPC per day).
   * Heroes (isHero=true) have no wage. NPCs have a wage property.
   * @param {Array} partyChars — includes hired NPCs
   * @returns {{ paid: number, shortfall: number }}
   */
  payWages(partyChars) {
    const npcs = partyChars.filter(c => !c.isHero && (c.wage ?? 0) > 0);
    const totalWages = npcs.reduce((sum, c) => sum + (c.wage ?? 0), 0);

    if (totalWages === 0) {
      return { paid: 0, shortfall: 0 };
    }

    const available = WorldState.partyGold ?? 0;
    if (totalWages <= available) {
      WorldState.partyGold = available - totalWages;
      DebugLog.info(`[camp] Wages paid: ${totalWages} gold (${npcs.length} NPCs)`);
      return { paid: totalWages, shortfall: 0 };
    }

    // Cannot fully pay wages
    const shortfall = totalWages - available;
    WorldState.partyGold = 0;

    DebugLog.warn(
      `[camp] Wage shortfall; needed=${totalWages} available=${available} shortfall=${shortfall}`
    );

    return { paid: available, shortfall };
  },

  // ─── Foraging ─────────────────────────────────────────────────────────────

  /**
   * Attempt foraging. Uses the best Nature or Survival modifier in the party.
   * DC varies by current biome.
   * Success: gain 1d4+1 ration points.
   * @param {Array} partyChars
   * @param {object} rng — RNG instance
   * @returns {{ success: boolean, gained: number, roll: number, dc: number, message: string }}
   */
  forage(partyChars, rng) {
    const biome = WorldState.currentBiome ?? 'forest';
    const dc    = FORAGE_DC[biome] ?? 10;

    // Find best Nature or Survival modifier across party
    let bestMod = 0;
    let bestCharName = 'Party';

    for (const char of partyChars) {
      // Use pre-computed skills if available, otherwise derive from attributes
      const natureMod   = char.skills?.nature   ?? this._deriveSkillMod(char, 'wisdom');
      const survivalMod = char.skills?.survival  ?? this._deriveSkillMod(char, 'wisdom');
      const mod = Math.max(natureMod, survivalMod);
      if (mod > bestMod) {
        bestMod = mod;
        bestCharName = char.name ?? 'Party member';
      }
    }

    // d20 roll + best modifier
    const d20 = rng?.nextInt?.(1, 20) ?? (1 + Math.floor(Math.random() * 20));
    const total = d20 + bestMod;
    const success = total >= dc;

    let gained = 0;
    if (success) {
      const d4 = rng?.nextInt?.(1, 4) ?? (1 + Math.floor(Math.random() * 4));
      gained = d4 + 1;
      WorldState.rationPoints = (WorldState.rationPoints ?? 0) + gained;
    }

    DebugLog.event(
      `[camp] Forage attempt; biome=${biome} dc=${dc} roll=${d20} mod=${bestMod} ` +
      `total=${total} success=${success} gained=${gained}; forager=${bestCharName}`
    );

    const message = success
      ? `${bestCharName} foraged successfully (rolled ${total} vs DC ${dc}). Gained ${gained} ration points.`
      : `Foraging failed (rolled ${total} vs DC ${dc}). Nothing found in the ${biome}.`;

    return { success, gained, roll: total, dc, message };
  },

  /**
   * Derive a simple skill modifier from an attribute when skills object is absent.
   * @param {object} char
   * @param {string} attr — attribute name ('wisdom', 'might', etc.)
   * @returns {number}
   */
  _deriveSkillMod(char, attr) {
    const attrVal = char.attributes?.[attr] ?? 10;
    return Math.floor((attrVal - 10) / 2);
  },

  // ─── Encounter chance ─────────────────────────────────────────────────────

  /**
   * Calculate night encounter chance based on camp quality.
   * Base: 10% + 3% per region level.
   * Reductions:
   *   scout role   -30%
   *   guard role   -20%
   *   tent         -10%
   *   alarm wire   -15%
   *   palisade     -20%
   * Minimum 1%, maximum 100%.
   * @returns {number} 0–1 probability
   */
  getNightEncounterChance() {
    const regionLevel = this._campState?.regionLevel ?? WorldState.currentRegionLevel ?? 1;
    const base = 0.10 + 0.03 * Math.max(0, regionLevel - 1);

    let reduction = 0;

    if (this._campState) {
      const { assignments, options } = this._campState;

      if (assignments?.some(a => a.role === 'scout'))  reduction += 0.30;
      if (assignments?.some(a => a.role === 'guard'))  reduction += 0.20;
      if (options?.hasTent)                             reduction += 0.10;
      if (options?.hasAlarmWire)                        reduction += 0.15;
      if (options?.hasPalisade)                         reduction += 0.20;
    }

    const chance = Math.max(0.01, Math.min(1.0, base - reduction));
    return chance;
  },

  // ─── Accessors ────────────────────────────────────────────────────────────

  /**
   * Get current camp state.
   * @returns {object|null}
   */
  getCampState() {
    return this._campState;
  },

  /**
   * Break camp. Clears camp state.
   */
  breakCamp() {
    DebugLog.info('[camp] Camp broken');
    this._campState = null;
  },

  // ─── Serialization ────────────────────────────────────────────────────────

  /**
   * Serialize camp state for save system.
   * @returns {{ campState: object|null }}
   */
  serialize() {
    return { campState: this._campState ? { ...this._campState } : null };
  },

  /**
   * Restore camp state from save data.
   * @param {object} data
   */
  deserialize(data) {
    this._campState = data?.campState ?? null;
    DebugLog.info(`[camp] CampingSystem deserialized; active=${this._campState?.active ?? false}`);
  },
};

// ─── Debug exposure ───────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.__campingSystem = CampingSystem;
}

export default CampingSystem;
